import { useEffect, useMemo } from 'react';

const ensureMetaTag = ({ name, property, content }) => {
  if (!content) return null;

  let selector = '';
  if (name) {
    selector = `meta[name="${name}"]`;
  } else if (property) {
    selector = `meta[property="${property}"]`;
  }

  let metaTag = selector ? document.head.querySelector(selector) : null;
  let created = false;

  if (!metaTag) {
    metaTag = document.createElement('meta');
    if (name) metaTag.setAttribute('name', name);
    if (property) metaTag.setAttribute('property', property);
    metaTag.dataset.reactSeo = 'true';
    document.head.appendChild(metaTag);
    created = true;
  }

  metaTag.setAttribute('content', content);
  return created ? metaTag : null;
};

const usePageMetadata = ({
  title,
  description,
  canonicalUrl,
  meta = [],
  jsonLd,
}) => {
  // Check if we're on dev site and add noindex meta tag
  const isDevSite = typeof window !== 'undefined' && (
    window.location.hostname.includes('dev.theadshop.co') || 
    window.location.hostname.includes('adshop.vercel.app')
  );

  const normalizedMeta = useMemo(() => {
    if (!meta) return [];
    const metaArray = Array.isArray(meta) ? meta : [meta];
    
    // Add noindex for dev site if not already present
    if (isDevSite) {
      const hasRobotsMeta = metaArray.some(m => 
        (m.name === 'robots' || m.property === 'robots')
      );
      if (!hasRobotsMeta) {
        return [...metaArray, { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet' }];
      }
    }
    
    return metaArray;
  }, [meta, isDevSite]);

  const normalizedJsonLd = useMemo(() => {
    if (!jsonLd) return [];
    return Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  }, [jsonLd]);

  const metaKey = useMemo(() => JSON.stringify(normalizedMeta), [normalizedMeta]);
  const jsonLdKey = useMemo(() => JSON.stringify(normalizedJsonLd), [normalizedJsonLd]);

  useEffect(() => {
    const createdMetaTags = [];

    if (title) {
      document.title = title;
    }

    if (description) {
      const created = ensureMetaTag({ name: 'description', content: description });
      if (created) createdMetaTags.push(created);
    }

    normalizedMeta.forEach((metaItem) => {
      const created = ensureMetaTag(metaItem);
      if (created) createdMetaTags.push(created);
    });

    let linkCanonical;
    if (canonicalUrl) {
      linkCanonical = document.head.querySelector('link[rel="canonical"]');
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonicalUrl);
    }

    const jsonLdScripts = [];
    normalizedJsonLd.forEach((schema) => {
      if (!schema) return;
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.reactSeo = 'true';
      script.innerHTML = JSON.stringify(schema);
      document.head.appendChild(script);
      jsonLdScripts.push(script);
    });

    return () => {
      createdMetaTags.forEach((tag) => {
        if (tag && tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });

      jsonLdScripts.forEach((script) => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [title, description, canonicalUrl, normalizedMeta, normalizedJsonLd, metaKey, jsonLdKey]);
};

export default usePageMetadata;


